import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Sparkles, QrCode, Monitor, Shield, ArrowRight, Play, Globe, Lock, Eye, Wine } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import FooterExpanded from '../components/landing/FooterExpanded';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function LandingPage() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const htmlLang = i18n.language && i18n.language.startsWith('pt')
    ? 'pt-PT'
    : (i18n.language && i18n.language.startsWith('es') ? 'es' : 'en');

  const [testimonials, setTestimonials] = useState([]);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/api/testimonials`, { params: { featured: true } })
      .then(r => { if (!cancelled) setTestimonials(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) setTestimonials([]); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-midnight overflow-hidden">
      <Helmet>
        <html lang={htmlLang} />
        <link rel="alternate" hrefLang="en" href="https://memouraa.com/" />
        <link rel="alternate" hrefLang="pt-PT" href="https://memouraa.com/?lang=pt-PT" />
        <link rel="alternate" hrefLang="es" href="https://memouraa.com/?lang=es" />
        <title>Memouraa — Capture the Aura</title>
        <meta name="description" content="Memouraa turns every guest's phone into a shared photo + voice + video portal. Memory + aura, captured." />
        <meta property="og:title" content="Memouraa — Capture the Aura" />
        <meta property="og:description" content="Every guest's phone becomes part of the memory. Photos, voice notes, video — all in one aura." />
        <meta property="og:image" content={`${API_URL}/api/og/default.png`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={`${API_URL}/api/og/default.png`} />
      </Helmet>
      {/* Animated background gradient */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-aura-purple/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-aura-gold/15 rounded-full blur-3xl animate-float" style={{ animationDelay: '-3s' }} />
      </div>

      {/* Navigation */}
      <nav className="relative z-50 glass-dark border-b border-champagne/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br from-aura-purple to-aura-gold flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl sm:text-2xl font-serif font-medium text-text-light tracking-tight truncate">Memouraa</span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-4">
            <Link
              to="/pricing"
              className="hidden sm:inline text-text-muted hover:text-champagne transition-colors font-medium"
              data-testid="nav-pricing-btn"
            >
              {t('common.pricing')}
            </Link>
            {user ? (
              <Link
                to="/dashboard"
                className="btn-champagne flex items-center gap-2 text-sm sm:text-base px-3 sm:px-4 py-2"
                data-testid="nav-dashboard-btn"
              >
                {t('common.dashboard')}
                <ArrowRight className="w-4 h-4" />
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-text-muted hover:text-champagne transition-colors font-medium text-sm sm:text-base"
                  data-testid="nav-login-btn"
                >
                  {t('common.signIn')}
                </Link>
                <Link
                  to="/register"
                  className="btn-champagne flex items-center gap-2 text-sm sm:text-base px-3 sm:px-4 py-2"
                  data-testid="nav-register-btn"
                >
                  <span className="hidden sm:inline">{t('common.create')}</span>
                  <span className="sm:hidden">{t('common.signUp')}</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-12 sm:pt-20 pb-20 sm:pb-32 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <span className="inline-block px-4 py-2 rounded-full bg-champagne/10 text-champagne text-sm font-medium mb-6 sm:mb-8 border border-champagne/20">
              {t('landing.heroBadge')}
            </span>
            <h1 className="text-4xl sm:text-7xl font-serif font-medium text-text-light mb-6 leading-tight">
              {t('landing.heroTitle')}{' '}
              <span className="italic bg-gradient-to-r from-aura-purple to-aura-gold bg-clip-text text-transparent">
                {t('landing.heroTitleAccent')}
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-text-muted leading-relaxed mb-8 sm:mb-10 max-w-2xl mx-auto">
              {t('landing.heroSubtitle')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/register" 
                className="btn-champagne flex items-center gap-2 text-lg px-8 py-4"
                data-testid="hero-create-btn"
              >
                <Sparkles className="w-5 h-5" />
                {t('landing.ctaPrimary')}
              </Link>
              <Link
                to="/portal/demo-memouraa-aura"
                className="btn-outline flex items-center gap-2 text-lg px-8 py-4"
                data-testid="hero-demo-btn"
              >
                <Play className="w-5 h-5" />
                {t('landing.ctaSecondary')}
              </Link>
            </div>
          </motion.div>

          {/* Hero Visual */}
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-20 relative"
          >
            <div className="aura-glow rounded-2xl overflow-hidden border border-champagne/20 bg-midnight-light">
              <div className="aspect-video relative">
                <img
                  src="https://images.pexels.com/photos/2747449/pexels-photo-2747449.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
                  alt="Elegant event"
                  className="w-full h-full object-cover opacity-80"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-midnight via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                  <div>
                    <p className="text-champagne font-medium mb-1">The Grand Gala 2026</p>
                    <p className="text-text-muted text-sm">247 moments captured</p>
                  </div>
                  <div className="flex gap-2">
                    <span className="mood-badge inline-flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> Magic</span>
                    <span className="mood-badge inline-flex items-center gap-1"><Wine className="w-3.5 h-3.5" /> Cheers</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 relative">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-5xl font-serif font-medium text-text-light mb-4">
              {t('landing.featuresHeading')}
            </h2>
            <p className="text-text-muted text-lg max-w-2xl mx-auto">
              {t('landing.featuresSubheading')}
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                icon: <QrCode className="w-8 h-8" />,
                title: "The Aura Key",
                description: "Generate stunning QR codes embedded in customizable Aura Cards. Print them, display them, let guests scan and contribute instantly.",
                tag: "Zero Friction"
              },
              {
                icon: <Sparkles className="w-8 h-8" />,
                title: "Mood Tagging",
                description: "Guests select a mood before uploading – Magic, Energy, Cheers, Love. Create an emotional map of your event.",
                tag: "Feel the Vibe"
              },
              {
                icon: <Monitor className="w-8 h-8" />,
                title: "AuraCast Live",
                description: "Project the living gallery in real-time. Watch new moments fade in with that signature aura glow effect.",
                tag: "Real-Time Magic"
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="aura-card rounded-xl p-6 sm:p-8"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-aura-purple/30 to-aura-gold/30 flex items-center justify-center text-champagne mb-6">
                  {feature.icon}
                </div>
                <span className="text-xs font-bold uppercase tracking-widest text-champagne mb-3 block">
                  {feature.tag}
                </span>
                <h3 className="text-2xl font-serif font-medium text-text-light mb-3">
                  {feature.title}
                </h3>
                <p className="text-text-muted leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Privacy Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-surface-dark">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-champagne text-sm font-bold uppercase tracking-widest mb-4 block">
                Your Rules, Your Aura
              </span>
              <h2 className="text-3xl sm:text-5xl font-serif font-medium text-text-light mb-6">
                Three Privacy Modes
              </h2>
              <div className="space-y-6">
                {[
                  { Icon: Globe, title: "Open", desc: "Anyone with the link can view and contribute freely" },
                  { Icon: Lock, title: "Locked", desc: "Password-protected – only those with the key can enter" },
                  { Icon: Eye, title: "Moderated", desc: "You approve every photo before it joins the gallery" }
                ].map((mode, i) => (
                  <div key={i} className="flex gap-4 items-start">
                    <mode.Icon className="w-6 h-6 text-champagne shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-lg font-medium text-text-light">{mode.title}</h4>
                      <p className="text-text-muted">{mode.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aura-glow rounded-2xl overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/3171837/pexels-photo-3171837.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
                  alt="Celebration"
                  className="w-full aspect-square object-cover opacity-80"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 aura-card rounded-xl p-4 flex items-center gap-3">
                <Shield className="w-8 h-8 text-champagne" />
                <div>
                  <p className="font-medium text-text-light">GDPR Ready</p>
                  <p className="text-sm text-text-muted">Privacy first</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-aura-purple/10 to-aura-gold/10" />
        <div className="max-w-3xl mx-auto text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-5xl font-serif font-medium text-text-light mb-6">
              Ready to capture your event's aura?
            </h2>
            <p className="text-lg sm:text-xl text-text-muted mb-10">
              Setup takes less than 60 seconds. No credit card required.
            </p>
            <Link 
              to="/register" 
              className="btn-champagne inline-flex items-center gap-2 text-lg px-10 py-5"
              data-testid="cta-create-btn"
            >
              Create Your First Aura
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="py-16 sm:py-24 px-4 sm:px-6 border-t border-champagne/10">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-champagne text-sm tracking-widest uppercase mb-4">Why Memouraa</p>
          <h2 className="font-serif text-3xl sm:text-4xl text-text-light mb-6">Named after a feeling.</h2>
          <p className="text-text-muted text-lg leading-relaxed max-w-2xl mx-auto mb-8">
            <em>Memória</em> is Portuguese for memory. <em>Aura</em> is the atmosphere a place keeps after the people leave. Memouraa is the hybrid — built by a small Canadian studio with Portuguese family roots, for the moments worth holding tighter.
          </p>
          <Link to="/about" className="inline-flex items-center gap-2 text-champagne hover:text-champagne-light font-medium">
            Read our story <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {testimonials.length > 0 && (
        <section className="py-16 sm:py-24 px-4 sm:px-6 border-t border-champagne/10" data-testid="landing-testimonials">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-serif text-3xl sm:text-4xl text-text-light text-center mb-12">
              {t('landing.testimonialsHeading')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.slice(0, 3).map((tm) => (
                <div
                  key={tm.id}
                  className="bg-white/[0.03] border border-white/10 rounded-xl p-6 flex flex-col"
                  data-testid={`landing-testimonial-${tm.id}`}
                >
                  <p className="italic text-text-light/90 leading-relaxed mb-4">“{tm.quote}”</p>
                  <div className="mt-auto">
                    <div className="text-champagne text-sm">— {tm.author_name}</div>
                    {tm.author_role && (
                      <div className="text-text-muted text-xs mt-1">{tm.author_role}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <FooterExpanded />
    </div>
  );
}
